import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FiCalendar, FiClock } from "react-icons/fi";
import Header from "../components/header/Header";
import ProgressBar from "../components/progressBar/ProgressBar";
import {
  CREATION_STEPS,
  SummaryBar,
  showError,
} from "../components/options/OptionParts";
import { addToCart } from "../redux/actions/CartActions";
import { updateBusinessRequest } from "../redux/actions/RequestActions";
import { formatPrice } from "../utils/formatPrice";
import {
  CLASSIC,
  EXPRESS,
  PROCEDURES,
  creationPrice,
  hasExpress,
  procedureFor,
} from "../utils/creationFlow";
import "./styles/options.css";

const DESCRIPTIONS = {
  [CLASSIC]: "Le traitement standard de votre dossier.",
  [EXPRESS]: "Votre dossier est traité en priorité par nos équipes.",
};

const ICONS = { [CLASSIC]: FiCalendar, [EXPRESS]: FiClock };

function Procedure() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);
  const { user } = useSelector((state) => state.authUser);
  const { businessCreationRequest } = useSelector((state) => state.request);

  const { businessType } = cart;
  const procedures = hasExpress(businessType) ? [CLASSIC, EXPRESS] : [CLASSIC];

  const [procedure, setProcedure] = useState(() =>
    procedureFor(businessType, cart.procedure),
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const price = creationPrice(businessType, procedure);

  const handleContinue = async () => {
    setSubmitting(true);

    await dispatch(
      updateBusinessRequest(
        user.token,
        businessCreationRequest?.reference,
        { procedure },
        handleSaved,
      ),
    );
  };

  const handleSaved = async ({ success, message }) => {
    setSubmitting(false);

    if (!success) {
      showError(Swal, message);
      return;
    }

    await dispatch(
      addToCart({
        procedure,
        creation: { businessType, amount: price },
      }),
    );

    navigate(businessType === "ETS" ? "/business-name" : "/share-capital");
  };

  return (
    <div className="options-page">
      <Header />
      <ProgressBar steps={CREATION_STEPS} currentStep="Entreprise" />

      <div className="container options-container">
        <header className="options-header">
          <h1 className="options-title">
            Choisissez votre <span>procédure</span>
          </h1>
          <p className="options-lead">
            La procédure détermine le délai de traitement de votre dossier une
            fois celui-ci complet.
          </p>
        </header>

        <div
          className="seat-choices procedure-choices"
          role="radiogroup"
          aria-label="Procédure"
        >
          {procedures.map((code) => {
            const Icon = ICONS[code];
            const selected = procedure === code;

            return (
              <button
                key={code}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`seat-choice ${selected ? "is-selected" : ""}`}
                onClick={() => setProcedure(code)}
              >
                <span className="option-icon" aria-hidden="true">
                  <Icon />
                </span>
                <span className="seat-choice-text">
                  <strong>{PROCEDURES[code].label}</strong>
                  <span>{DESCRIPTIONS[code]}</span>
                  <span className="procedure-meta">
                    {PROCEDURES[code].delay} ·{" "}
                    <b>{formatPrice(creationPrice(businessType, code))}</b>
                  </span>
                </span>
                <span className="seat-radio" aria-hidden="true" />
              </button>
            );
          })}
        </div>

        {procedures.length === 1 && (
          <p className="procedure-note">
            La procédure express n'est pas disponible pour une {businessType}.
          </p>
        )}
      </div>

      <SummaryBar
        label={`Création d'une ${businessType} · ${PROCEDURES[procedure].label}`}
        total={formatPrice(price)}
        buttonLabel={submitting ? "Enregistrement…" : "Continuer"}
        disabled={submitting}
        onContinue={handleContinue}
      />
    </div>
  );
}

export default Procedure;
